import React, { useEffect, useReducer, Reducer, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { faHouseWindow } from "@fortawesome/pro-regular-svg-icons";
import { MainContainer } from "../../components/layout";
import UserContext from "../../state/UserContext";
import JourneyDashboardWidget from "../../components/journeys/JourneyDashboardWidget";
import MyFormSummaries from "../../components/form-summaries/MyFormSummaries";
import MyDashboardApiResponseDto from "../../types/dtos/dashboards/MyDashboardApiResponseDto";
import JourneyFormGroupDto from "../../types/dtos/journeys/JourneyFormGroupDto";
import AppContext from "../../state/AppContext";
import { BaseUserDetailsDto } from "../../types/dtos/generic";
import DashboardSummaryTabDto from "../../types/dtos/dashboards/DashboardSummaryTabDto";
import { ErrorPopup, ModalPopup } from "../../components/common";
import { interactionHelper } from "../../helpers";
import permissionHelper from "../../helpers/permissionHelper";
import myDashboardApi from "../../api/dashboard/myDashboardApi";
import { EditableTask } from "../../types/tasks/EditableTasks";
import ModifyTaskResponseDto from "../../types/dtos/tasks/ModifyTaskResponseDto";
import { TaskType } from "../../types/tasks";
import { EmployeeDashboardUpdateDto } from "../../types/dtos/dashboards";
import MyDashWalkthrough from "../../components/user-walkthrough/MyDashWalkthrough";
import userApi from "../../api/user/userApi";
import AppRoutes from "../AppRoutes";
import { JourneyAssignmentResponseDto } from "../../types/dtos/journeys";
import SmallLoader from "../../components/loaders/SmallLoader";
import GenericApiResult from "../../types/dtos/generic/GenericApiResult";
import { JourneyDeadlineMessage } from "../../components/journeys/JourneyDeadlineMessage";
import JourneySwitcher from "../../components/journeys/JourneySwitcher";
import DashboardRightBar from "../../components/dashboard/right-bar/DashboardRightBar";
import DashboardRightBarDto from "../../types/dtos/dashboards/right-bar/DashboardRightBarDto";
import UserJourneyDetailDto from "../../types/dtos/shared/UserJourneyDetailDto";
import LoadingDashboardRightBarPlaceholder from "../../components/dashboard/right-bar/LoadingDashboardRightBarPlaceholder";
import NextJourney from "../../components/dashboard/NextJourney";
import { NextJourneyDto } from "../../types/journeys/NextJourney";
import ContentDashboard from "../../components/content-dashboard/ContentDashboard";
import { ContentDashboardType } from "../../types/admin/dashboard/ContentDashboardType";
import type ContentDashboardDto from "../../types/dtos/content-dashboard/ContentDashboardDto";
import { ContentDashboardStatus } from "../../types/admin/dashboard/ContentDashboardStatus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faChevronDown,
} from "@fortawesome/pro-solid-svg-icons";
import { faEye, faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { Link } from "react-router-dom";
import dashboardManagementApi from "../../api/admin/dashboardManagementApi";
import { ToastConfig } from "../../components/common/ToastConfig";
import Toast from "../../components/common/Toast";
import type { ContentDashboardListItemDto } from "../../types/dtos/content-dashboard/ContentDashboardListItemDto";
import type { KeyValuePair } from "../../types/generic";
import PendingFeedbackWidget from "../../components/collab-docs/feedback/PendingFeedbackWidget";
import moduleAccessHelper from "../../helpers/moduleAccessHelper";

interface YourDashboardState {
  waitingForApiResult: boolean;
  userFormSummaries: DashboardSummaryTabDto[];
  journeyFormDetails: JourneyFormGroupDto | undefined | null;
  journeyUpdates: EmployeeDashboardUpdateDto[];
  showUpdates: boolean;
  summariesLoading: boolean;
  subjectUser: BaseUserDetailsDto | null;
  /** Used when we need the user to confirm they want to change their current journey */
  proposedNewJourneyClientFormId: number | null;
  proposedNewDualPrepJourney: string | null;
  showNewJourneyConfirmPrompt: boolean;
  showCannotChangeLockedJourneyPrompt: boolean;
  firstPageLoadCompleted: boolean;
  allowWalkthrough: boolean;
  showPreparingDocumentModal: boolean;
  switchableJourneys: UserJourneyDetailDto[];
  rightBarDetails: DashboardRightBarDto | null;
  /** Whether the API includes managerTeamTodos (drives loading skeleton on reload). */
  showManagerTeamSection: boolean;
  nextJourney: NextJourneyDto | null;
  contentDashboard: ContentDashboardDto | null;
}

function YourDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const userContext = React.useContext(UserContext);
  const appContext = React.useContext(AppContext);
  const dashboardApi = new myDashboardApi(auth.user?.access_token);

  // Preview mode state
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [previewDashboardId, setPreviewDashboardId] = useState<number | null>(null);
  const [availableDashboards, setAvailableDashboards] = useState<ContentDashboardListItemDto[]>([]);
  const [assignedDashboardId, setAssignedDashboardId] = useState<number | null>(null);
  const [availableBrands, setAvailableBrands] = useState<KeyValuePair<number, string>[] | null>(null);
  const [currentBrandId, setCurrentBrandId] = useState<number | null>(null);
  const [currentBrandName, setCurrentBrandName] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const [state, setState] = useReducer<
    Reducer<YourDashboardState, Partial<YourDashboardState>>
  >((state, newState) => ({ ...state, ...newState }), {
    waitingForApiResult: true,
    userFormSummaries: [],
    journeyFormDetails: null,
    journeyUpdates: [],
    showUpdates: true,
    summariesLoading: true,
    subjectUser: null,
    proposedNewJourneyClientFormId: null,
    proposedNewDualPrepJourney: null,
    showNewJourneyConfirmPrompt: false,
    firstPageLoadCompleted: false,
    allowWalkthrough: true,
    showPreparingDocumentModal: false,
    showCannotChangeLockedJourneyPrompt: false,
    switchableJourneys: [],
    rightBarDetails: null,
    showManagerTeamSection: false,
    nextJourney: null,
    contentDashboard: null,
  });

  const [dashboardMainLoadError, setDashboardMainLoadError] =
    useState<boolean>(false);
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
  const [errorPopupDetails, setErrorPopupDetails] = useState<any | null>(null);
  const [hasJourneyBeenSubmitted, setHasJourneyBeenSubmitted] =
    useState<boolean>(false);
  const [showManageCatchUpMeetingScreen, setShowManageCatchUpMeetingScreen] =
    useState<boolean>(false);
  const [showActivateDashboardConfirm, setShowActivateDashboardConfirm] =
    useState<boolean>(false);
  const [isActivatingDashboard, setIsActivatingDashboard] =
    useState<boolean>(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>(
    ToastConfig.hidden()
  );
  const usersApi = new userApi(auth.user?.access_token);

  // Conditionally hide if the user's dashboard mode is "EXIT" or the module config
  // "PreventManualJourneySwitching" is set to "true"
  const hideJourneySwitcher =
    userContext.user.myDashboardMode === "EXIT" ||
    userContext.user.client.moduleConfigs.findIndex(
      (cfg) =>
        cfg.key === "PreventManualJourneySwitching" &&
        cfg.value.trim().toLowerCase() === "true"
    ) >= 0;

  const feedbackModuleEnabled = moduleAccessHelper.userHasModule(
    userContext.user,
    "Feedback Requests"
  );

  const reloadDashboard = () => {
    setShowErrorPopup(false);
    setDashboardMainLoadError(false);
    setJourneySubmitted(false);
    interactionHelper.deFocusJourney();

    // Check if we're in preview mode from URL param
    const previewIdParam = searchParams.get("previewDashboardId");
    const previewId = previewIdParam ? parseInt(previewIdParam, 10) : null;

    // Only honor preview URL param when user has permission to manage content dashboards
    const userCanManageContentDashboards =
      userContext.user.client.permissionMode === "LEGACY-PERMISSIONS"
        ? permissionHelper.userHasPermission(
            userContext,
            "ManageContentDashboards"
          )
        : permissionHelper.userHasPermission(
            userContext,
            "MANAGE-CONTENT-DASHBOARDS"
          );
    const canHonorPreviewParam = userCanManageContentDashboards && previewId != null;

    const successCallback = (data: MyDashboardApiResponseDto) => {
      const ctx = data.contentDashboardContext;
      const assignedId = ctx?.assignedContentDashboardId ?? null;
      const availableList = ctx?.availableContentDashboards ?? [];

      setAssignedDashboardId(assignedId);
      setAvailableDashboards(availableList);
      setAvailableBrands(ctx?.availableBrands ?? null);
      setCurrentBrandId(ctx?.currentBrandId ?? null);
      setCurrentBrandName(ctx?.currentBrandName ?? null);

      const assignedDashboard = ctx?.assignedContentDashboard ?? null;

      // If URL had preview param but user lacks permission, clear the URL and show assigned dashboard
      if (previewId != null && !userCanManageContentDashboards) {
        navigate(AppRoutes.yourJourney.root, { replace: true });
      }

      // When URL has previewDashboardId and user has permission, show that dashboard (load by ID if different from assigned)
      if (canHonorPreviewParam && previewId !== assignedId) {
        setIsPreviewMode(true);
        setPreviewDashboardId(previewId);
        setState({
          userFormSummaries: data.summaries,
          summariesLoading: false,
          journeyFormDetails: data.journey,
          waitingForApiResult: false,
          journeyUpdates: data.updates,
          showUpdates: true,
          firstPageLoadCompleted: true,
          switchableJourneys: data.otherJourneys,
          rightBarDetails: data.rightBarDetails,
          showManagerTeamSection: data.rightBarDetails?.managerTeamTodos != null,
          nextJourney: data.nextJourney,
          contentDashboard: null,
        });
        dashboardApi.getContentDashboardById(
          previewId,
          (previewDashboard) => {
            setState({ contentDashboard: previewDashboard });
          },
          () => {
            setState({ contentDashboard: assignedDashboard });
            setIsPreviewMode(false);
            setPreviewDashboardId(null);
          }
        );
        return;
      }

      // No preview in URL, or preview not allowed, or preview is the assigned dashboard
      let dashboardToDisplay: ContentDashboardDto | null = assignedDashboard;
      let isInPreviewMode = false;
      let previewingDashboardId: number | null = null;

      if (canHonorPreviewParam && previewId === assignedId) {
        isInPreviewMode = true;
        previewingDashboardId = previewId;
      } else if (assignedDashboard?.dashboardStatus === ContentDashboardStatus.Draft) {
        isInPreviewMode = true;
        previewingDashboardId = assignedDashboard.dashboardId;
      }

      setIsPreviewMode(isInPreviewMode);
      setPreviewDashboardId(previewingDashboardId);

      setState({
        userFormSummaries: data.summaries,
        summariesLoading: false,
        journeyFormDetails: data.journey,
        waitingForApiResult: false,
        journeyUpdates: data.updates,
        showUpdates: true,
        firstPageLoadCompleted: true,
        switchableJourneys: data.otherJourneys,
        rightBarDetails: data.rightBarDetails,
        showManagerTeamSection: data.rightBarDetails?.managerTeamTodos != null,
        nextJourney: data.nextJourney,
        contentDashboard: dashboardToDisplay,
      });
    };

    const errorCallback = (error: any) => {
      console.error(error);
      setDashboardMainLoadError(true);
      setShowErrorPopup(true);
      setErrorPopupDetails(error);
    };

    dashboardApi.getFullDashboard(
      ContentDashboardType.EmployeeDashboard,
      successCallback,
      errorCallback
    );
  };

  const reloadSummaries = () => {
    setShowErrorPopup(false);
    setDashboardMainLoadError(false);

    // Call the API to load the necessary state
    const successCallback = (summaries: DashboardSummaryTabDto[]) => {
      setState({
        userFormSummaries: summaries,
      });
    };

    const errorCallback = (error: any) => {
      console.error(error);
      setShowErrorPopup(true);
      setErrorPopupDetails(error);
    };

    dashboardApi.getSummaries(successCallback, errorCallback);
  };

  const setJourneySubmitted = (newValue: boolean) => {
    setHasJourneyBeenSubmitted(newValue);

    // If the newValue is true then we should reload the summaries
    if (newValue) {
      reloadSummaries();
    }
  };

  const refreshLoggedInUserDetails = (onlyRefreshIfStale: boolean) => {
    // Conditionally, only refresh if the user details are more than 15 seconds old,
    // to prevent unnecessary API calls when the user lands on this homepage
    if (onlyRefreshIfStale && userContext.user.dateLoaded) {
      // Bomb out if we work out it's stale
      const maxAgeInSeconds = 15;
      const secondsSinceLastRefresh =
        (new Date().getTime() - userContext.user.dateLoaded.getTime()) / 1000;
      if (secondsSinceLastRefresh < maxAgeInSeconds) return;
    }

    // Call the API to reload parts of the user's details
    usersApi.getRefreshedLoggedInUserDetails(
      userContext.setUpdatedUserDetails,
      () => {}
    );
  };

  useEffect(() => {
    reloadDashboard();
    appContext.setPageTitle(t("Pages.MyDashboard.PageTitle"));
    appContext.setPageIcon(faHouseWindow);
    appContext.setActiveDashboardTypeForWalkthrough("MY-DASHBOARD");
    appContext.setShowPageTitleAccent(true);
    refreshLoggedInUserDetails(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadJourneyWidget = () => {
    // Reload the whole dashboard, as summaries and tasks may have changed too
    reloadDashboard();
  };

  const handleSummaryRefresh = () => {
    reloadDashboard();
  };

  /** Call the API and find and assign the relevant journey to the user,
   * and return the new journey details, and refresh the displayed journey
   */
  const assignDifferentJourneyToUser = (
    clientFormId: number | null,
    exactJourneyRef: string | null,
    optionalSuccessCallback?: undefined | (() => void),
    optionalErrorCallback?: undefined | (() => void)
  ) => {
    const onSuccess = (assignmentResult: JourneyAssignmentResponseDto) => {
      if (assignmentResult?.redirectToAnswerSet) {
        // Redirect to the answer set
        const redirectUrl = AppRoutes.collaborativeDocument.generateRoute(
          assignmentResult.redirectToAnswerSet,
          false,
          null
        );
        navigate(redirectUrl);
      } else if (assignmentResult?.newJourney) {
        setState({
          journeyFormDetails: assignmentResult.newJourney,
          waitingForApiResult: false,
          showUpdates: false,
          switchableJourneys: assignmentResult.otherJourneys,
        });
      } else {
        setState({
          waitingForApiResult: false,
        });
        setShowErrorPopup(true);
        setErrorPopupDetails(
          assignmentResult?.errorMessage ??
            "Couldn't assign the requested journey to the current user"
        );
      }

      interactionHelper.scrollMainContainerToTop();

      if (optionalSuccessCallback) {
        optionalSuccessCallback();
      }
    };

    const onError = (error: any) => {
      console.error("Unable to change journey", error);
      setShowErrorPopup(true);
      setErrorPopupDetails(error);

      if (optionalErrorCallback) {
        optionalErrorCallback();
      }
    };

    // Reset the submitted prop
    setJourneySubmitted(false);

    // Show the spinner
    setState({
      waitingForApiResult: true,
    });

    dashboardApi.assignDifferentJourneyToUser(
      clientFormId,
      exactJourneyRef,
      onSuccess,
      onError
    );
  };

  /**Call the api to Add/Edit/Delete task object*/
  const modifyTaskFromDashboard = (
    task: EditableTask<number>,
    successCallback: (taskData: ModifyTaskResponseDto<number>) => void
  ) => {
    const onError = (error: any) => {
      console.log("Unable to update task", error);
    };

    let modifyTaskType = "ADD";
    if (task.taskId != null) {
      modifyTaskType = "EDIT";
    }

    dashboardApi.modifyTaskFromDashboard(
      task,
      modifyTaskType,
      successCallback,
      onError
    );
  };

  /**Call the api to load the editable task */
  const loadEditableTaskDetails = (
    taskId: number,
    taskType: TaskType,
    successCallback: (data: ModifyTaskResponseDto<number>) => void
  ) => {
    const onError = (error: any) => {
      console.log("Unable to update task", error);
    };
    dashboardApi.loadEditableTaskDetails(
      taskId,
      taskType,
      successCallback,
      onError
    );
  };

  function onUserClickedSummaryUpdateButton(
    summaryTab: DashboardSummaryTabDto,
    exactJourneyRef: string | null
  ) {
    const hasBeenUpdatedBefore = !!summaryTab.lastCompletedDate;
    if (hasBeenUpdatedBefore) {
      // User has completed this form before, so take them to the most recent completed version
      // so they can use that as a base to edit
      if (summaryTab.lastCompletedAnswerSetId) {
        const redirectUrl = AppRoutes.collaborativeDocument.generateRoute(
          summaryTab.lastCompletedAnswerSetId,
          false,
          summaryTab.clientFormId
        );
        navigate(redirectUrl);
      } else {
        console.error("Logical flaw... completed, but no completed answer set");
      }
    } else {
      // User has never completed this form before, so trigger it in the journey UI,
      // unless the current journey is locked (i.e. has a future deadline)

      // If the user is on a journey, then we need to prompt them to confirm they want to start a new one
      // If they're not on a journey, we can just assign the new one
      handleManualNewJourneyAssignment(
        summaryTab.clientFormId,
        exactJourneyRef
      );
    }
  }

  /** When the journey needs to be reloaded, because the user has selected to start a new journey
   * from within a summary section
   */
  function handleManualNewJourneyAssignment(
    clientFormId: number | null,
    exactJourneyRef: string | null,
    optionalSuccessCallback?: undefined | (() => void),
    optionalErrorCallback?: undefined | (() => void)
  ) {
    if (!state.journeyFormDetails) {
      // No journey, can assign a new one straight away
      assignDifferentJourneyToUser(
        clientFormId,
        exactJourneyRef,
        optionalSuccessCallback,
        optionalErrorCallback
      );
      return;
    }

    if (
      // Form is part of the current dual prep journey, or...
      (exactJourneyRef &&
        exactJourneyRef.length > 0 &&
        state.journeyFormDetails?.journeyReference === exactJourneyRef) ||
      // Form is the one already being completed
      (!exactJourneyRef &&
        clientFormId !== null &&
        state.journeyFormDetails?.forms &&
        state.journeyFormDetails.forms.length === 1 &&
        state.journeyFormDetails.forms[0].formId === clientFormId)
    ) {
      // Form id is already being completed, just scroll the user to the journey and focus it
      interactionHelper.scrollMainContainerToTop();
      interactionHelper.focusJourney();
    } else {
      // There is a journey already
      // If the current journey has a deadline, then we can't change the assigned journey
      const currentJourneyIsLocked =
        state.journeyFormDetails.journeyDeadline &&
        new Date(state.journeyFormDetails.journeyDeadline) > new Date();

      if (currentJourneyIsLocked) {
        setState({ showCannotChangeLockedJourneyPrompt: true });
        return;
      }

      assignDifferentJourneyToUser(
        clientFormId,
        exactJourneyRef,
        optionalSuccessCallback,
        optionalErrorCallback
      );
    }
  }

  const handleChangeToSpecificJourney = (
    journeyRef: string,
    optionalSuccessCallback?: undefined | (() => void),
    optionalErrorCallback?: undefined | (() => void)
  ) => {
    if (
      state.journeyFormDetails &&
      state.journeyFormDetails.journeyReference === journeyRef
    ) {
      // The intended journey is already assigned
      setState({ showUpdates: false });
      interactionHelper.scrollMainContainerToTop();
    } else {
      // The journey is changing, so call the API to change it
      handleManualNewJourneyAssignment(
        null,
        journeyRef,
        optionalSuccessCallback,
        optionalErrorCallback
      );
    }
  };

  const setJourneyChangePromptVisibility = (isVisible: boolean) => {
    setState({ showNewJourneyConfirmPrompt: isVisible });
    if (!isVisible) {
      // Reset to clear the proposed journey
      setState({
        proposedNewJourneyClientFormId: null,
        proposedNewDualPrepJourney: null,
      });
    }
  };

  const setJourneyIsLockedPromptVisibility = (isVisible: boolean) => {
    setState({ showCannotChangeLockedJourneyPrompt: isVisible });
  };

  const startCreateAndPrefillCollabDocProcess = () => {
    if (state.journeyFormDetails != null) {
      setShowPreparingDocumentModal(true);
      const journeyDetails = state.journeyFormDetails;

      const successCallback = (data: GenericApiResult) => {
        if (data.successful) {
          const destinationUrl = AppRoutes.collaborativeDocument.generateRoute(
            data.data as string,
            false,
            null
          );
          navigate(destinationUrl);
        }
      };

      const errorCallback = (error: any) => {
        console.error(error);
      };

      dashboardApi.createAndPrefillDoc(
        journeyDetails.subjectUser.userId,
        journeyDetails.journeyReference,
        successCallback,
        errorCallback
      );
    }
  };

  const setShowPreparingDocumentModal = (isVisible: boolean) => {
    setState({ showPreparingDocumentModal: isVisible });
  };

  const confirmJourneyChange = () => {
    // If a valid form/journey is specified, call the API to change the journey
    if (
      (state.proposedNewJourneyClientFormId &&
        state.proposedNewJourneyClientFormId > 0) ||
      (state.proposedNewDualPrepJourney &&
        state.proposedNewDualPrepJourney.length > 0)
    ) {
      // Load the new form
      assignDifferentJourneyToUser(
        state.proposedNewJourneyClientFormId,
        state.proposedNewDualPrepJourney
      );

      // Hide the modal and clear the state variable for the proposed new form
      setJourneyChangePromptVisibility(false);
    }
  };

  const cancelJourneyChange = () => {
    // Hide the modal and clear the state variable for the proposed new form
    setJourneyChangePromptVisibility(false);
  };

  /** Control whether or not to display the list of updates
   * within the journey widget (e.g. a task to complete a collab doc)
   */
  const onToggleJourneyWidgetUpdatesVisibility = (
    shouldShowUpdates: boolean
  ) => {
    setState({ showUpdates: shouldShowUpdates });
  };

  // Get the count of summary tabs displayed on the dashboard
  let userSummaryTabCount = 0;
  if (userContext.user.myDashboardMode !== "EXIT" && state.userFormSummaries) {
    userSummaryTabCount = state.userFormSummaries.length;
  }

  const handleWalkthroughFinished = () => {
    setState({ allowWalkthrough: false });
  };

  // Handle activating dashboard from draft banner
  const handleActivateDashboard = () => {
    setShowActivateDashboardConfirm(true);
  };

  const confirmActivateDashboard = () => {
    if (!state.contentDashboard) return;

    setIsActivatingDashboard(true);
    const api = new dashboardManagementApi(auth.user?.access_token);

    api.updateDashboardStatus(
      state.contentDashboard.dashboardId,
      ContentDashboardType.EmployeeDashboard,
      ContentDashboardStatus.Active,
      userContext.user.client.hasBrandsEnabled,
      (success) => {
        const activatedId = state.contentDashboard!.dashboardId;
        setState({
          contentDashboard: state.contentDashboard
            ? {
                ...state.contentDashboard,
                dashboardStatus: ContentDashboardStatus.Active,
              }
            : null,
        });
        setAssignedDashboardId(activatedId);
        setAvailableDashboards((prev) =>
          prev.map((d) => ({
            ...d,
            dashboardStatus:
              d.dashboardId === activatedId
                ? ContentDashboardStatus.Active
                : ContentDashboardStatus.Draft,
          }))
        );
        setIsActivatingDashboard(false);
        setShowActivateDashboardConfirm(false);
        setToastConfig(
          ToastConfig.success(
            "Dashboard Activated",
            "This dashboard is now live and visible to all users."
          )
        );
      },
      (error) => {
        console.error("Error activating dashboard:", error);
        setIsActivatingDashboard(false);
        setShowActivateDashboardConfirm(false);
        setToastConfig(
          ToastConfig.error(
            "Activation Failed",
            "Failed to activate the dashboard. Please try again."
          )
        );
      }
    );
  };

  const cancelActivateDashboard = () => {
    setShowActivateDashboardConfirm(false);
  };

  const handlePreviewDashboardChange = (newDashboardId: number) => {
    navigate(`${AppRoutes.yourJourney.root}?previewDashboardId=${newDashboardId}`, { replace: true });
    setIsLoadingPreview(true);

    dashboardApi.getContentDashboardById(
      newDashboardId,
      (data) => {
        setState({ contentDashboard: data });
        setPreviewDashboardId(newDashboardId);
        setIsPreviewMode(true);
        setIsLoadingPreview(false);
      },
      () => {
        setIsLoadingPreview(false);
      }
    );
  };

  const handleExitPreview = () => {
    navigate(AppRoutes.yourJourney.root, { replace: true });
    setIsPreviewMode(false);
    setPreviewDashboardId(null);
    setIsLoadingPreview(true);

    dashboardApi.getFullDashboard(
      ContentDashboardType.EmployeeDashboard,
      (data) => {
        const ctx = data.contentDashboardContext;
        setState({ contentDashboard: ctx?.assignedContentDashboard ?? null });
        setAssignedDashboardId(ctx?.assignedContentDashboardId ?? null);
        setAvailableDashboards(ctx?.availableContentDashboards ?? []);
        setAvailableBrands(ctx?.availableBrands ?? null);
        setCurrentBrandId(ctx?.currentBrandId ?? null);
        setCurrentBrandName(ctx?.currentBrandName ?? null);
        setIsLoadingPreview(false);
      },
      () => {
        setIsLoadingPreview(false);
      }
    );
  };

  return (
    <MainContainer
      rightColumnChildren={
        <>
          {state.waitingForApiResult && (
            <LoadingDashboardRightBarPlaceholder
              showManagerTeamSection={state.showManagerTeamSection}
            />
          )}
          {!state.waitingForApiResult && state.rightBarDetails && (
            <DashboardRightBar
              details={state.rightBarDetails}
              formDetails={state.journeyFormDetails}
              hasJourneyBeenSubmitted={hasJourneyBeenSubmitted}
              onManagerTodosChanged={reloadDashboard}
              modifyStandardTaskFromDashboard={modifyTaskFromDashboard}
              onLoadStandardTaskToEdit={loadEditableTaskDetails}
            />
          )}
        </>
      }
    >
      {!hideJourneySwitcher && (
        <JourneySwitcher
          onJourneyChange={handleChangeToSpecificJourney}
          currentJourneyRef={state.journeyFormDetails?.journeyReference}
          otherJourneys={state.switchableJourneys}
        />
      )}

      <div className={hideJourneySwitcher ? "mt-2 lg:mt-4" : ""}>
        <JourneyDashboardWidget
          formDetails={state.journeyFormDetails}
          isLoading={state.waitingForApiResult}
          onLoadNextJourney={reloadJourneyWidget}
          hasJourneyBeenSubmitted={hasJourneyBeenSubmitted}
          onJourneySubmissionSuccess={setJourneySubmitted}
          showManageCatchUpMeetingScreen={showManageCatchUpMeetingScreen}
          onShowManageCatchUpMeetingScreenChange={
            setShowManageCatchUpMeetingScreen
          }
          userSummaryTabCount={userSummaryTabCount}
          updates={state.journeyUpdates}
          showUpdates={state.showUpdates}
          onToggleUpdatesVisibility={onToggleJourneyWidgetUpdatesVisibility}
          onChangeJourney={handleChangeToSpecificJourney}
          createAndPrefillCollabDoc={startCreateAndPrefillCollabDocProcess}
        />
      </div>

      {feedbackModuleEnabled && (
        <div className="mt-4">
          <PendingFeedbackWidget />
        </div>
      )}

      {state.nextJourney && <NextJourney journey={state.nextJourney} />}

      {/* Content Dashboard */}
      <div className={state.nextJourney ? "mt-6" : "mt-8"}>
        {/* Preview Mode Banner - Shows when user is in preview mode */}
        {isPreviewMode && state.contentDashboard && (
          <div className="mb-4 bg-gray-50 border border-gray-300 rounded-lg p-4">
            <div className="flex gap-3">
              {/* Eye Icon */}
              <div className="flex-shrink-0 pt-1">
                <FontAwesomeIcon icon={faEye} className="text-gray-600" size="lg" />
              </div>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {/* Preview Mode Title */}
                <div className="text-base font-semibold text-gray-900 mb-1">Preview Mode</div>

                {/* Description */}
                <div className="text-xs text-gray-700 mb-1.5">Select a dashboard to preview:</div>

                {/* Dropdown + Exit Button Row */}
                <div className="flex items-center gap-3 mb-1.5">
                  {/* Custom Dropdown */}
                  <div className="flex-1 relative">
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 ring-primary flex items-center justify-between"
                    >
                      <div className="flex items-center justify-between flex-1 gap-2">
                        <span className="truncate">
                          {(() => {
                            const currentItem = availableDashboards.find(
                              d => d.dashboardId === state.contentDashboard?.dashboardId
                            );
                            const isAssigned = currentItem?.dashboardId === assignedDashboardId;
                            return `${currentItem?.dashboardName ?? "Select dashboard"}${isAssigned ? " (Currently Assigned)" : ""}`;
                          })()}
                        </span>
                        <span className={`inline-flex items-center px-1.5 py-0 rounded font-medium whitespace-nowrap text-xxs ${
                          state.contentDashboard.dashboardStatus === ContentDashboardStatus.Active
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}>
                          {state.contentDashboard.dashboardStatus === ContentDashboardStatus.Active ? "ACTIVE" : "DRAFT"}
                        </span>
                      </div>
                      <FontAwesomeIcon icon={faChevronDown} className="ml-2 text-gray-400" size="sm" />
                    </button>
                    
                    {isDropdownOpen && (
                      <>
                        {/* Backdrop */}
                        <div 
                          className="fixed inset-0 z-10" 
                          onClick={() => setIsDropdownOpen(false)}
                        />
                        
                        {/* Dropdown Menu */}
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                          {availableDashboards.map((dashboard) => {
                            const isAssigned = dashboard.dashboardId === assignedDashboardId;
                            const isSelected = dashboard.dashboardId === state.contentDashboard?.dashboardId;
                            
                            return (
                              <button
                                key={dashboard.dashboardId}
                                onClick={() => {
                                  handlePreviewDashboardChange(dashboard.dashboardId);
                                  setIsDropdownOpen(false);
                                }}
                                className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center justify-between gap-2 ${
                                  isSelected ? "background-tint-bg" : ""
                                }`}
                              >
                                <span className="truncate">
                                  {dashboard.dashboardName}{isAssigned ? " (Currently Assigned)" : ""}
                                </span>
                                <span className={`inline-flex items-center px-1.5 py-0 rounded font-medium whitespace-nowrap text-xxs ${
                                  dashboard.dashboardStatus === ContentDashboardStatus.Active
                                    ? "bg-green-100 text-green-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}>
                                  {dashboard.dashboardStatus === ContentDashboardStatus.Active ? "ACTIVE" : "DRAFT"}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Exit Preview Button */}
                  <button
                    onClick={handleExitPreview}
                    className="btn-secondary btn-small whitespace-nowrap"
                  >
                    Exit Preview
                  </button>
                </div>

                {/* Brand Assignment Info */}
                {userContext.user.client.hasBrandsEnabled && (() => {
                  const currentItem = availableDashboards.find(
                    d => d.dashboardId === state.contentDashboard?.dashboardId
                  );
                  const assignedBrandNames = (currentItem?.assignedBrandIds ?? [])
                    .map(id => availableBrands?.find(b => b.key === id)?.value)
                    .filter((name): name is string => Boolean(name));
                  const isAssignedToCurrentBrand = currentBrandId != null && (currentItem?.assignedBrandIds?.includes(currentBrandId) ?? false);

                  return (
                    <div className="text-xs text-gray-600 flex items-center gap-2">
                      {assignedBrandNames.length > 0 ? (
                        <>
                          <span>Assigned to: </span>
                          {assignedBrandNames.map((brandName, idx) => (
                            <span key={idx}>
                              <strong>{brandName}</strong>
                              {idx < assignedBrandNames.length - 1 ? ", " : ""}
                            </span>
                          ))}
                        </>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          Unassigned
                        </span>
                      )}
                      {currentBrandName != null && !isAssignedToCurrentBrand && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                          Not assigned to {currentBrandName}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Draft Dashboard Banner - Shows when dashboard status is Draft */}
        {state.contentDashboard &&
          state.contentDashboard.dashboardStatus === ContentDashboardStatus.Draft && (
            <div className="mb-4 bg-yellow-50 border border-yellow-300 rounded-lg p-4">
              <div className="flex items-start justify-between">
                 <div className="flex items-center gap-3">
                   <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600" size="lg" />
                   <div>
                     <div className="text-base font-semibold text-yellow-900 mb-1">Draft Dashboard</div>
                     <div className="text-xs text-yellow-800">
                       This dashboard is in draft mode and not visible to users
                     </div>
                   </div>
                 </div>
                 {!userContext.user.client.hasBrandsEnabled && (
                   <button
                     onClick={handleActivateDashboard}
                     disabled={isActivatingDashboard}
                     className="px-4 py-2 text-sm font-medium bg-white text-yellow-900 border border-yellow-300 rounded-md hover:bg-yellow-50 transition-colors disabled:opacity-50"
                   >
                     Activate Dashboard
                   </button>
                 )}
                 {userContext.user.client.hasBrandsEnabled && (
                   <Link
                     to={AppRoutes.admin.dashboards.path}
                     className="px-4 py-2 text-sm font-medium bg-white text-yellow-900 border border-yellow-300 rounded-md hover:bg-yellow-50 transition-colors inline-flex items-center gap-2"
                   >
                     Manage in Dashboard Management
                   </Link>
                 )}
              </div>
            </div>
          )}

        {/* Content Dashboard with Loading Overlay */}
        <div className="relative">
          {isLoadingPreview && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg min-h-[200px]">
              <div className="text-center">
                <SmallLoader colour={userContext.user.client.primaryHexColour} />
                <p className="text-sm text-gray-600 mt-3">Loading dashboard...</p>
              </div>
            </div>
          )}
          <ContentDashboard
            dashboardData={state.contentDashboard}
            isLoading={state.waitingForApiResult}
          />
        </div>
      </div>

      <div className="mt-6">
        <MyFormSummaries
          summaries={state.userFormSummaries}
          isLoading={state.summariesLoading}
          onTriggerFormFromSummaryTab={onUserClickedSummaryUpdateButton}
          handleSummaryRefresh={handleSummaryRefresh}
          modifyTaskFromDashboard={modifyTaskFromDashboard}
          onLoadGoalToEdit={loadEditableTaskDetails}
        />
      </div>

      {/* Activate Dashboard Confirmation Modal */}
      <ModalPopup
        isOpen={showActivateDashboardConfirm}
        onOpenChange={setShowActivateDashboardConfirm}
        title="Activate Dashboard"
        primaryButtonText="Activate"
        secondaryButtonText="Cancel"
        onPrimaryButtonClick={confirmActivateDashboard}
        onSecondaryButtonClick={cancelActivateDashboard}
        isLoading={isActivatingDashboard}
        isPrimaryButtonDisabled={isActivatingDashboard}
        width="SMALL"
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to activate this dashboard? It will become
          visible to all users immediately.
        </p>
      </ModalPopup>

      {/* Confirm journey change prompt */}
      <ModalPopup
        isOpen={state.showNewJourneyConfirmPrompt}
        onOpenChange={setJourneyChangePromptVisibility}
        onPrimaryButtonClick={cancelJourneyChange}
        primaryButtonText={t(
          "Pages.MyDashboard.JourneyChangePrompt.PrimaryButtonText"
        )}
        title={t("Pages.MyDashboard.JourneyChangePrompt.Title")}
        secondaryButtonText={t(
          "Pages.MyDashboard.JourneyChangePrompt.SecondaryButtonText"
        )}
        onSecondaryButtonClick={confirmJourneyChange}
        width="SMALL"
      >
        <p>{t("Pages.MyDashboard.JourneyChangePrompt.Body")}</p>
      </ModalPopup>

      <ModalPopup
        isOpen={state.showPreparingDocumentModal}
        onOpenChange={setShowPreparingDocumentModal}
        title={t("Pages.MyDashboard.PreparingYourDocModal.Title")}
        showCloseIcon={false}
        width="SMALL"
      >
        <div className="text-center pt-4">
          <SmallLoader colour={userContext.user.client.accentHexColour} />
          <p className="mt-4 mb-2">
            {t("Pages.MyDashboard.PreparingYourDocModal.Body")}
          </p>
        </div>
      </ModalPopup>

      <ModalPopup
        isOpen={state.showCannotChangeLockedJourneyPrompt}
        onOpenChange={setJourneyIsLockedPromptVisibility}
        title={t("Pages.MyDashboard.JourneyLockedPrompt.Title")}
        width="SMALL"
        onPrimaryButtonClick={() => {
          setJourneyIsLockedPromptVisibility(false);
        }}
        primaryButtonText={t("Common.OK")}
      >
        <p className="py-4">
          {t("Pages.MyDashboard.JourneyLockedPrompt.Body")}
          {state.journeyFormDetails?.journeyDeadline && (
            <JourneyDeadlineMessage
              classnames="ml-1"
              deadline={state.journeyFormDetails.journeyDeadline}
            />
          )}
        </p>
      </ModalPopup>

      <ErrorPopup
        error={errorPopupDetails}
        onTogglePopup={setShowErrorPopup}
        showPopup={showErrorPopup}
        customClickEvent={dashboardMainLoadError ? reloadDashboard : undefined}
      />

      {state.firstPageLoadCompleted &&
        state.allowWalkthrough &&
        userContext.user.walkthroughSeen != null &&
        !userContext.user.walkthroughSeen.includes("MY-DASHBOARD") && (
          <MyDashWalkthrough
            hasClientGotAdvancedTaskTypes={
              userContext.user.client.taskTypes.length > 0
            }
            onFinished={handleWalkthroughFinished}
          />
        )}

      {/* Toast notifications */}
      {toastConfig.show && (
        <Toast
          title={toastConfig.title}
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={() => setToastConfig(ToastConfig.hidden())}
        />
      )}
    </MainContainer>
  );
}

export default YourDashboard;
